import { useContext } from 'react';
import styled, { css } from 'styled-components';

import Badge from '../../../dashboard/components/common/base/Badge';
import Button from '../../../dashboard/components/common/base/Button';
import Icon from '../../../dashboard/components/common/base/Icon';
import Text from '../../../dashboard/components/common/base/Text';
import Tooltip from '../../../dashboard/components/common/base/Tooltip';
import DisplayDate from '../../../dashboard/components/common/DisplayDate';
import StyledEllipsisOverflow from '../../../dashboard/components/common/helpers/EllipsisOverflow';
import { Div } from '../../../dashboard/components/common/helpers/StyledUtils';
import { Status } from '../../../dashboard/components/common/Status';
import Table from '../../../dashboard/components/common/Table/Table';
import { rejection_cause_labels } from '../../../dashboard/utils/rejection-causes';
import { ConsoleEntriesContext } from '../context/ConsoleEntriesContext';
import { ConsoleResourceContext } from '../context/ConsoleResourceContext';
import { ConsoleSidebarContext } from '../context/ConsoleSidebarContext';
import { getCurrentTimezoneAbreviation } from '../../../dashboard/utils/date';
import isEmpty from '../../../dashboard/utils/isEmpty';

const StyledTableWrapper = styled.div(
  ({ theme }) => css`
    background-color: ${theme.colors.surface.base.surface};
    border-bottom: ${theme.border};
  `,
);
const ConsoleEntriesTable: React.FC = () => {
  const { entries, sendRequest, retryEvent } = useContext(ConsoleEntriesContext);
  const { selectEventOrRequest, selected_entry, editDestination } =
    useContext(ConsoleSidebarContext);
  const { webhook } = useContext(ConsoleResourceContext);

  const rows = entries?.map((entry) => ({
    id: entry.id,
    selectable: true,
    selected:
      selected_entry?.id === entry.id ||
      ('request_id' in entry && selected_entry?.id === entry.request_id),
    fields: [
      {
        clickable_area: true,
        element: (
          <div>
            <DisplayDate date={entry.created_at} show_timezone={false} />
          </div>
        ),
      },
      'status' in entry ? (
        <Div flex={{ justify: 'space-between', align: 'center' }}>
          <Status {...entry} />
          <Tooltip tooltip="Resend to destination" side="bottom">
            <Button
              small
              ghost
              muted
              icon="retry"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                retryEvent(entry.id);
              }}
            />
          </Tooltip>
        </Div>
      ) : (
        {
          clickable_area: true,
          element:
            entry.rejection_cause && entry.rejection_cause !== 'NO_CONNECTION' ? (
              <Tooltip tooltip={rejection_cause_labels[entry.rejection_cause]}>
                <Badge danger>
                  <Icon icon="error" left />
                  <StyledEllipsisOverflow>Error</StyledEllipsisOverflow>
                </Badge>
              </Tooltip>
            ) : (
              <Div flex={{ justify: 'space-between', align: 'center' }}>
                <Badge icon="success_circle" neutral>
                  Accepted
                </Badge>
                {!isEmpty(webhook) ? (
                  <Tooltip tooltip="Send to destination" side="bottom">
                    <Button
                      ghost
                      muted
                      small
                      icon="send"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        sendRequest(entry, webhook);
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip tooltip="Set a destination and send request" side="bottom">
                    <Button
                      small
                      ghost
                      muted
                      icon="send"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        editDestination();
                      }}
                    />
                  </Tooltip>
                )}
              </Div>
            ),
        }
      ),
      {
        clickable_area: true,
        element: (
          <Text mono w={100}>
            <StyledEllipsisOverflow>{JSON.stringify(entry.data?.body)}</StyledEllipsisOverflow>
          </Text>
        ),
      },
    ],
  }));

  if (!entries || !rows) {
    return null;
  }

  return (
    <Div>
      <StyledTableWrapper>
        <Table
          fill
          onRowSelected={(id) => {
            const entry = entries.find((entry) => entry.id === id);
            if (!entry) {
              return;
            }
            selectEventOrRequest(id, entry);
          }}
          headers={[`Entry Date (${getCurrentTimezoneAbreviation()})`, 'Status', 'Body']}
          widths={[{ min: 150, max: 150 }, { min: 174, max: 174 }, { min: 100 }]}
          top_offset={0}
          rows={rows}
        />
      </StyledTableWrapper>
      {entries.length === 250 && (
        <Div flex={{ align: 'center' }} m={{ t: 4 }} p={{ x: 4 }}>
          <Icon icon="info" small muted left />
          <Text muted>The console displays the latest 250 events.</Text>
        </Div>
      )}
    </Div>
  );
};

export default ConsoleEntriesTable;
